import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  RoutingPolicyChoice,
  RoutingPolicyChoiceKey,
  RoutingPolicyDetail,
} from "@shared-core/data/manufacturing-setup-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  formatChoiceLastChanged,
  siteTownName,
} from "@shared-core/domain/manufacturing-setup-presentation";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Textarea } from "../app/components/primitives/textarea";
import { Check, Factory, History, Loader2 } from "lucide-react";

/**
 * Settings › Manufacturing › Routing policy (design note
 * `docs/features/cost-engine-productionisation/03_manufacturing_surfaces_design.md`
 * §"Routing policy page"): the engine's fixed routing rules, the small set
 * of admin-changeable choices with their version history, and the two
 * derived read-only tables that explain which equipment lives where and
 * which finishing steps are feasible on which site.
 */
export function LiveCostingRoutingPolicyPage() {
  const adapter = useDataAdapter();
  const [detail, setDetail] = useState<RoutingPolicyDetail | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pendingChoice, setPendingChoice] =
    useState<RoutingPolicyChoice | null>(null);
  const [pendingValue, setPendingValue] = useState<string>("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<"newValue" | "note", string>>
  >({});

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .getRoutingPolicy()
      .then(setDetail)
      .catch(() => setLoadError(true));
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  function openChangeDialog(choice: RoutingPolicyChoice) {
    setFieldErrors({});
    setPendingChoice(choice);
    setPendingValue(choice.currentValue);
    setNote("");
  }

  async function submitChange() {
    if (!pendingChoice) return;
    setSubmitting(true);
    setFieldErrors({});
    try {
      const updated = await adapter.changeRoutingPolicyChoice({
        // The wire's `RoutingPolicyChoiceSchema.key` is a plain `string`;
        // narrowed to the frontend's closed key union here, at the one
        // seam that reads it off a fetched choice (manufacturing-setup-types.ts's
        // own docstring on `RoutingPolicyChoiceKey`).
        key: pendingChoice.key as RoutingPolicyChoiceKey,
        newValue: pendingValue,
        note,
      });
      setDetail(updated);
      toast.success(`${pendingChoice.label} updated`);
      setPendingChoice(null);
      setNote("");
    } catch (err) {
      const fieldErrorList = extractFieldErrors(err);
      if (fieldErrorList.length > 0) {
        const byField: Partial<Record<"newValue" | "note", string>> = {};
        for (const fe of fieldErrorList) {
          byField[fe.field as "newValue" | "note"] = fe.message;
        }
        setFieldErrors(byField);
      } else {
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to change routing policy",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <div className="space-y-2">
        <PageHeading />
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-center text-sm text-destructive">
          Couldn&rsquo;t load the routing policy. Please try again.
        </p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-2">
        <PageHeading />
        <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          Loading…
        </p>
      </div>
    );
  }

  const newValueLabel =
    pendingChoice?.options.find((o) => o.value === pendingValue)?.label ?? "";
  const isChanging =
    pendingChoice && pendingValue !== pendingChoice.currentValue;

  return (
    <div className="space-y-6">
      <PageHeading />

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
        <span>
          Version {detail.current.version} · changed by{" "}
          <span className="font-medium">{detail.current.changedBy}</span>{" "}
          {formatDateTimeOrDash(detail.current.changedAt)} &ldquo;
          {detail.current.note}&rdquo;
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setHistoryOpen(true)}
        >
          <History className="size-4 mr-2" />
          History
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rules (fixed)</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="grid gap-2 sm:grid-cols-2">
            {detail.fixedRules.map((rule) => (
              <li key={rule.key} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <span>{rule.label}</span>
              </li>
            ))}
          </ul>
          <ExcludedPressesConsequence
            equipmentBySite={detail.equipmentBySite}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Choices (admin)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {detail.choices.map((choice) => (
            <div
              key={choice.key}
              className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3 last:border-0 last:pb-0"
            >
              <div>
                <div className="text-sm font-medium">{choice.label}</div>
                <div className="text-sm text-muted-foreground">
                  {choice.currentLabel}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatChoiceLastChanged(
                    choice.lastChangedNote,
                    choice.lastChangedBy,
                    choice.lastChangedAt,
                  )}
                </div>
              </div>
              {choice.isFixed ? (
                <Badge variant="outline">fixed</Badge>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openChangeDialog(choice)}
                >
                  Change
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Equipment → site
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                live from standards
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cost centre</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.equipmentBySite.map((eq) => (
                    <TableRow key={eq.costCentreId}>
                      <TableCell className="font-medium">
                        {eq.costCentreId} {eq.code}
                        {eq.name !== eq.code && (
                          <div className="text-xs font-normal text-muted-foreground">
                            {eq.name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {siteTownName(eq.site)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {!eq.isPress ? (
                          <span className="text-muted-foreground">—</span>
                        ) : eq.excludedFromRouting ? (
                          <Badge variant="warning">
                            Excluded: not yet used in any price check
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">
                            Used in {eq.priceChecksUsedIn}{" "}
                            {eq.priceChecksUsedIn === 1
                              ? "price check"
                              : "price checks"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Per-step feasible sites
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                derived
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Finishing step</TableHead>
                    <TableHead>Feasible sites</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.stepFeasibleSites.map((step) => (
                    <TableRow key={step.processTypeId}>
                      <TableCell className="font-medium">
                        {step.stepName}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {step.sites.join(", ")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Routing policy history</DialogTitle>
          </DialogHeader>
          <ul className="space-y-3 max-h-[60vh] overflow-y-auto">
            {detail.history.map((entry) => (
              <li
                key={entry.version}
                className="border-b border-border pb-3 last:border-0"
              >
                <div className="text-sm font-medium">
                  Version {entry.version} · {entry.changedBy} ·{" "}
                  {formatDateTimeOrDash(entry.changedAt)}
                </div>
                {entry.whatChanged && (
                  <div className="text-sm text-muted-foreground">
                    {entry.whatChanged}
                  </div>
                )}
                <div className="text-sm text-muted-foreground italic">
                  &ldquo;{entry.note}&rdquo;
                </div>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingChoice !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingChoice(null);
            setNote("");
            setFieldErrors({});
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change &ldquo;{pendingChoice?.label}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Items whose routes change will show Re-check needed until the next
              rolling validation run.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {Object.keys(fieldErrors).length > 0 && (
            <p role="alert" className="text-sm text-destructive">
              {Object.keys(fieldErrors).length === 1
                ? "1 field needs attention below."
                : `${Object.keys(fieldErrors).length} fields need attention below.`}
            </p>
          )}
          <div className="space-y-1.5">
            <select
              aria-label={`New value for ${pendingChoice?.label ?? "choice"}`}
              aria-invalid={fieldErrors.newValue != null}
              aria-describedby={
                fieldErrors.newValue
                  ? "routing-policy-new-value-error"
                  : undefined
              }
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
              value={pendingValue}
              onChange={(e) => setPendingValue(e.target.value)}
            >
              {pendingChoice?.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {fieldErrors.newValue && (
              <p
                id="routing-policy-new-value-error"
                className="text-xs text-destructive"
              >
                {fieldErrors.newValue}
              </p>
            )}
            {isChanging && (
              <p className="text-xs text-muted-foreground">
                {pendingChoice?.currentLabel} → {newValueLabel}
              </p>
            )}
          </div>
          <Textarea
            placeholder="Note (required)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-invalid={fieldErrors.note != null}
            aria-describedby={
              fieldErrors.note ? "routing-policy-note-error" : undefined
            }
          />
          {fieldErrors.note && (
            <p
              id="routing-policy-note-error"
              className="text-xs text-destructive"
            >
              {fieldErrors.note}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!note.trim() || !isChanging || submitting}
              onClick={(e) => {
                // Radix's AlertDialogAction closes the dialog on click by
                // default; preventDefault keeps it open so a server-side
                // validation error can render next to the offending field
                // instead of the dialog silently vanishing. `submitChange()`
                // closes it itself on success.
                e.preventDefault();
                void submitChange();
              }}
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Confirm"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const EXCLUDED_PRESSES_COLLAPSED_COUNT = 5;

/**
 * The consequence of "never route to a press not yet used in any price
 * check" (renamed from "that has never been checked" — coordinator review
 * round 4) — which presses it currently excludes, linking to the same
 * by-cost-centre data (design decision, `03_manufacturing_surfaces_design.md`
 * "Routing policy shows the never-checked rule with the presses it currently
 * excludes, linking to the by-cost-centre view"). Collapsed to the first 5
 * behind a toggle — the rule is press-only now (coordinator review round 4),
 * but a real estate can still exclude more than fits comfortably in a list.
 */
function ExcludedPressesConsequence({
  equipmentBySite,
}: {
  equipmentBySite: RoutingPolicyDetail["equipmentBySite"];
}) {
  const [showAll, setShowAll] = useState(false);
  const excluded = equipmentBySite.filter((eq) => eq.excludedFromRouting);
  if (excluded.length === 0) return null;
  const hiddenCount = excluded.length - EXCLUDED_PRESSES_COLLAPSED_COUNT;
  const visible =
    showAll || hiddenCount <= 0
      ? excluded
      : excluded.slice(0, EXCLUDED_PRESSES_COLLAPSED_COUNT);
  return (
    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900/50 dark:bg-amber-900/20">
      <p className="font-medium">
        Currently excludes {excluded.length}{" "}
        {excluded.length === 1 ? "press" : "presses"} not yet used in any price
        check:
      </p>
      <ul className="mt-1 list-inside list-disc text-muted-foreground">
        {visible.map((eq) => (
          <li key={eq.costCentreId}>
            {eq.costCentreId} {eq.code} · {siteTownName(eq.site)}
          </li>
        ))}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-1 text-[color:var(--color-primary)] hover:underline"
        >
          {showAll ? "Show fewer" : `and ${hiddenCount} more`}
        </button>
      )}
      <Link
        to={paths.settingsLiveCostingChecks({ view: "cost-centre" })}
        className="mt-1 block text-[color:var(--color-primary)] hover:underline"
      >
        See the by-cost-centre view
      </Link>
    </div>
  );
}

function PageHeading() {
  return (
    <div>
      <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
        <Factory className="h-6 w-6" /> Routing policy
      </h1>
      <p className="text-sm text-muted-foreground mt-1">
        The rules the engine uses to choose a route, and the admin choices that
        govern the cases the rules alone don&rsquo;t settle.
      </p>
    </div>
  );
}
